// Contas puras da organização (testadas em node: web/test/organize.test.ts).
import { normalizeTag } from "../../../../shared/normalize.ts";

/** Cores das coleções (desenho próprio: tons de terra do fai.g + vivos). A primeira livre é a sugestão da nova. */
export const SPACE_COLORS: [string, string][] = [
  ["#D4A343", "Pelagem"], ["#C2593A", "Terracota"], ["#E5866A", "Coral"], ["#D9799B", "Rosa"],
  ["#8E4E7E", "Ameixa"], ["#9C8AD0", "Lavanda"], ["#4C5FA8", "Anil"], ["#5FA3C9", "Céu"],
  ["#3E9C9A", "Lagoa"], ["#52633E", "Folhas"], ["#7E9B4C", "Musgo"], ["#B5C24A", "Lima"],
  ["#E2B93B", "Mostarda"], ["#B49A72", "Duna"], ["#765034", "Manchas"], ["#5C5F58", "Grafite"],
];
export const colorName = (hex: string) => SPACE_COLORS.find(([h]) => h.toLowerCase() === hex.toLowerCase())?.[1] ?? hex;
/** Primeira cor ainda sem coleção (todas usadas: recomeça pela ordem). */
export function nextColor(used: string[]): string {
  const taken = new Set(used.map((c) => c.toLowerCase()));
  return (SPACE_COLORS.find(([h]) => !taken.has(h.toLowerCase())) ?? SPACE_COLORS[used.length % SPACE_COLORS.length])[0];
}

/** Nome já usado por outra coleção (mesma regra do servidor: sem caixa nem acento). */
export const nameTaken = (spaces: { id: string; normalized: string }[], name: string, except?: string) =>
  spaces.some((s) => s.id !== except && s.normalized === normalizeTag(name));

/**
 * Posição para `id` ir ao índice `to` da ordem atual (`order`, ids em ordem; `pos`, posição de cada um).
 * Fica no meio dos vizinhos; nas pontas, um a menos/a mais. null se não muda.
 */
// ponytail: posição fracionária (metade da distância); depois de ~50 movimentos no mesmo vão perde precisão, renumerar se surgir.
export function moveTo(order: string[], pos: ReadonlyMap<string, number | null>, id: string, to: number): number | null {
  const from = order.indexOf(id);
  if (from < 0) return null;
  const rest = order.filter((x) => x !== id);
  to = Math.max(0, Math.min(rest.length, to));
  if (to === from) return null;
  const at = (i: number) => (i >= 0 && i < rest.length ? (pos.get(rest[i]) ?? null) : null);
  const before = at(to - 1);
  const after = at(to);
  if (before === null && after === null) return 1;
  if (before === null) return after! - 1;
  if (after === null) return before + 1;
  return (before + after) / 2;
}

/** Ids de `a` a `b` (inclusive) na ordem da grade, em qualquer sentido. Sem um deles, só `b`. */
export function rangeIds(order: string[], a: string | null, b: string): string[] {
  const i = a === null ? -1 : order.indexOf(a);
  const j = order.indexOf(b);
  if (i < 0 || j < 0) return [b];
  return order.slice(Math.min(i, j), Math.max(i, j) + 1);
}

/** "1 card" / "3 cards". */
export const cardsLabel = (n: number) => (n === 1 ? "1 card" : `${n} cards`);

// Mesmo recorte de tokens da busca (TOKEN em web/src/search/parser.ts, que não é exportado).
const QUERY_TOKEN = /(-?)(#|\p{L}+:)?"([^"]*)"?|\||[^\s|"]+/gu;
/**
 * Consulta salva com a etiqueta `from` (normalizada; `#x` solto ou `#"x y"`) trocada por `#to`. Antes do "~" só,
 * como a busca lê. null se a consulta não usa a etiqueta.
 */
// ponytail: nome com aspas não tem forma na gramática (#"…" não escapa aspas); sai entre aspas e deixa de casar.
export function retagQuery(query: string, from: string, to: string): string | null {
  const cut = query.includes("~") ? query.indexOf("~") : query.length;
  let hit = false;
  const head = query.slice(0, cut).replace(QUERY_TOKEN, (tok, neg: string | undefined, key: string | undefined, quoted: string | undefined) => {
    const name = quoted !== undefined ? (key === "#" ? quoted : null) : tok[0] === "#" ? tok.slice(1) : null;
    if (name === null || normalizeTag(name) !== from) return tok;
    hit = true;
    return quoted !== undefined || /[\s|"]/.test(to) ? `${neg ?? ""}#"${to}"` : `#${to}`;
  });
  return hit ? head + query.slice(cut) : null;
}
